import { useState } from 'react';
import { Container, Nav, Navbar } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logoWebp from './../../../assets/img/logo-header.webp';
import logo from './../../../assets/img/logo-header.jpg';
import { useAuthContext } from '../../../context/AuthProvider';
import { canEnterAdmin, canEnterPanel } from '../../../utils/access';
import { scrollToSection } from '../../../utils/scrollToSection';

/**
 * Enlace a una sección de la home.
 *
 * Las secciones (#inicio, #about, ...) solo existen en la página de inicio. Si
 * el usuario está en otra ruta —la vitrina de modelos, un perfil, un 404— no
 * hay nada a lo que desplazarse: hay que navegar primero a la home y hacer el
 * scroll una vez montada. El destino viaja en el state de la navegación.
 */
function AnchorLink({ target, children, onNavigate }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleClick = (event) => {
    event.preventDefault();

    if (pathname === '/') {
      scrollToSection(target);
    } else {
      navigate('/', { state: { scrollTo: target } });
    }

    if (onNavigate) onNavigate();
  };

  return (
    <Nav.Link href={`#${target}`} onClick={handleClick}>
      {children}
    </Nav.Link>
  );
}

export default function Header() {
  const { user, logout } = useAuthContext();
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const close = () => setExpanded(false);

  const exitUser = () => {
    logout();
    close();
    navigate('/');
  };

  // Desde otra ruta el logo debe llevar a la home, no solo subir el scroll.
  const goHome = (event) => {
    event.preventDefault();
    if (pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
    close();
  };

  return (
    <header id="header">
      {/* `xl` y no `lg`: a 992px los diez enlaces del menú no caben en una
          línea y se salían del contenedor por la derecha. */}
      <Navbar
        expand="xl"
        className="custom-nav p-0"
        expanded={expanded}
        onToggle={setExpanded}
      >
        <Container>
          <Navbar.Brand href="/" onClick={goHome}>
            <div id="logo" className="pull-left">
              {/* El CSS lo limita a 130 px de alto: basta con 320 px para
                  pantallas 2x. El original de 3544 px pesaba 1,4 MB y se
                  descargaba en todas y cada una de las páginas. */}
              <picture>
                <source srcSet={logoWebp} type="image/webp" />
                <img
                  src={logo}
                  alt="Afrodita Models — estudio de modelaje webcam en Armenia"
                  title="Afrodita Models"
                  width={286}
                  height={320}
                  fetchpriority="high"
                />
              </picture>
            </div>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="responsive-navbar-nav" />
          <Navbar.Collapse id="responsive-navbar-nav">
            <Nav className="me-auto"></Nav>
            <Nav className='nav-menu'>
              <AnchorLink target="inicio" onNavigate={close}>Inicio</AnchorLink>
              <AnchorLink target="about" onNavigate={close}>Nosotros</AnchorLink>
              <AnchorLink target="services" onNavigate={close}>Servicios</AnchorLink>
              <AnchorLink target="portfolio" onNavigate={close}>Galería</AnchorLink>
              <AnchorLink target="team" onNavigate={close}>Equipo</AnchorLink>
              <AnchorLink target="contact" onNavigate={close}>Contacto</AnchorLink>

              {/* Acceso unificado y destacado a Modelos / En Vivo con Live Badge pulsante */}
              <Nav.Link as={Link} to="/modelos" onClick={close} className="nav-link-models-live">
                <span className="nav-live-indicator" aria-hidden="true">
                  <span className="nav-live-dot"></span>
                  <i className="fa-solid fa-video" />
                </span>
                <span className="nav-models-label">Modelos</span>
                <span className="nav-live-badge-tag">EN VIVO</span>
              </Nav.Link>

              {/* Acceso directo al panel según permisos */}
              {canEnterAdmin(user) && (
                <Nav.Link as={Link} to="/admin" onClick={close} className="nav-admin">
                  <i className="fa-solid fa-shield-halved me-1" aria-hidden="true" /> Administración
                </Nav.Link>
              )}

              {!canEnterAdmin(user) && canEnterPanel(user) && (
                <Nav.Link as={Link} to="/panel" onClick={close} className="nav-admin">
                  <i className="fa-solid fa-id-card me-1" aria-hidden="true" /> Mi perfil
                </Nav.Link>
              )}

              {user ? (
                <>
                  <span className="nav-user-name" title={`Sesión iniciada como ${user.name}`}>
                    <i className="fa-solid fa-circle-user me-1" aria-hidden="true" /> {user.name}
                  </span>
                  <Nav.Link as="button" type="button" onClick={exitUser} className="nav-logout" title="Cerrar sesión">
                    <i className="fa-solid fa-right-from-bracket me-1" aria-hidden="true" /> Salir
                  </Nav.Link>
                </>
              ) : (
                <Nav.Link as={Link} to="/login" onClick={close} className="nav-btn-login">
                  <i className="fa-solid fa-user me-1" aria-hidden="true" /> Ingresar
                </Nav.Link>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>
    </header>
  );
}
